import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { PROGRAMS } from "../data/content";
import { SCHEDULER_URL, ASSETS } from "../lib/constants";

export default function ProgramDetail() {
  const { slug } = useParams();
  const prog = PROGRAMS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!prog) {
    return (
      <div className="grain min-h-screen">
        <Nav />
        <div className="pt-40 px-6 max-w-3xl mx-auto text-center">
          <h1 className="font-serif text-4xl">Program not found</h1>
          <Link to="/" className="inline-block mt-6 text-primary-c font-mono text-xs tracking-[0.2em] uppercase">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="program-detail">
      <Nav />
      {/* Hero */}
      <section className="relative pt-36 md:pt-48 pb-20 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(90% 70% at 20% 0%, rgba(var(--glow),0.18), transparent 60%)" }} />
        <div className="relative max-w-5xl mx-auto">
          <Link to="/#programs" data-testid="detail-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/50 hover:text-fg transition-colors mb-10">
            <ArrowLeft size={14} /> All Programs
          </Link>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c mb-4">{prog.index} · {prog.label}</p>
          {prog.logo && (
            prog.logoLight ? (
              <div className="inline-block bg-white rounded-2xl p-4 mb-5 shadow-lg" data-testid="detail-logo-wrap">
                <img src={prog.logo} alt={`${prog.name} logo`} data-testid="detail-logo" className="w-36 md:w-44 h-auto" />
              </div>
            ) : (
              <img src={prog.logo} alt={`${prog.name} badge`} data-testid="detail-logo" className="w-28 md:w-36 h-auto mb-5 drop-shadow-[0_0_24px_rgba(var(--glow),0.45)]" />
            )
          )}
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight">{prog.name}</h1>
          <p className="mt-6 max-w-2xl text-fg/70 md:text-xl leading-relaxed">{prog.hero}</p>
          <div className="mt-8 inline-flex items-baseline gap-3">
            <span className="font-serif text-4xl text-primary-c">{prog.stat.value}</span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">{prog.stat.label}</span>
          </div>
          {prog.image && (
            <div className="mt-12 shape-r overflow-hidden border border-white/10" data-testid="detail-hero-image">
              <img src={prog.image} alt={prog.name} className="w-full max-h-[60vh] object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* What Happens */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-12">What happens in the room</h2>
          <div className="space-y-px bg-white/10 shape-r overflow-hidden">
            {prog.whatHappens.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-bg p-8 flex gap-6 items-start"
              >
                <span className="font-mono text-primary-c text-sm">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-fg/75 leading-relaxed md:text-lg">{step}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Great For */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Great for</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prog.greatFor.map((a) => (
              <div key={a} className="glass shape-r p-6 flex items-center gap-4">
                <span className="text-primary-c"><Check size={18} /></span>
                <span className="font-medium">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {prog.partner && (
        <section className="py-16 md:py-20 px-6 md:px-10 border-t border-white/5" data-testid="detail-partner">
          <div className="max-w-4xl mx-auto glass shape-r p-8 md:p-10">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-primary-c mb-3">In Partnership With</p>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{prog.partner.name}</h3>
            <p className="text-fg/70 leading-relaxed md:text-lg">{prog.partner.note}</p>
            <a
              href={prog.partner.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-partner-link"
              className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] tracking-[0.2em] uppercase text-fg hover:text-primary-c transition-colors"
            >
              Visit {prog.partner.name} <ArrowRight size={15} />
            </a>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute inset-0" style={{ background: "radial-gradient(80% 100% at 50% 50%, rgba(var(--glow),0.14), transparent 65%)" }} />
          <div className="relative">
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Bring {prog.name} to your space.</h2>
            <p className="mt-5 text-fg/70 md:text-lg max-w-xl mx-auto">When words fall short, rhythm speaks. Let's design a gentle session for the people you serve.</p>
            <a href={SCHEDULER_URL} target="_blank" rel="noopener noreferrer" data-testid="detail-book-btn" className="inline-flex items-center gap-2 mt-8 shape-r bg-primary-c text-bg font-medium px-8 py-4 hover:opacity-90 transition-opacity">
              Book a Cadence Call <ArrowRight size={17} />
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
